/**
 * PhoneInput — a phone field with a fixed, non-editable `+91` country prefix.
 *
 * The user types only the 10 local digits; `+91` is shown as a static prefix and can't be
 * edited or deleted. A whole number pasted or autofilled with +91 / 91 / 0 in front becomes its
 * 10 local digits (AUTH-16). The component reports the FULL E.164 value (`+91XXXXXXXXXX`) via
 * `onChange`.
 *
 * Validation (Indian mobile): exactly 10 digits starting with 6/7/8/9. The input enforces this
 * via a native `pattern` (so a `required` form won't submit an invalid number) and shows an
 * inline hint + red border once 10 digits are entered but the rule isn't met.
 */

import { useEffect, useRef } from "react";

import { isValidLocal, localDigits, typedLocal } from "../../lib/phone";

const CC = "+91";

export interface PhoneInputProps {
  value: string;
  onChange: (e164: string) => void;
  label?: string;
  required?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
}

export function PhoneInput({
  value,
  onChange,
  label,
  required,
  placeholder = "XXXXXXXXXX",
  autoFocus,
  className = "",
}: PhoneInputProps) {
  const local = localDigits(value);
  const ref = useRef<HTMLInputElement>(null);

  // Show "complete but invalid" only once the user has typed all 10 digits.
  const invalid = local.length === 10 && !isValidLocal(local);

  // Native validity message so a required form won't submit a bad number.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.setCustomValidity(
      local.length > 0 && !isValidLocal(local)
        ? "Enter a 10-digit mobile number starting with 6, 7, 8, or 9."
        : "",
    );
  }, [local]);

  const handle = (raw: string) => onChange(`${CC}${typedLocal(raw)}`);

  const field = (
    <div
      className={`flex items-stretch overflow-hidden rounded-xl border bg-surface shadow-xs transition focus-within:ring-2 ${
        invalid
          ? "border-danger focus-within:border-danger focus-within:ring-danger/25"
          : "border-line hover:border-line-strong focus-within:border-brand focus-within:ring-brand/25"
      } ${className}`}
    >
      <span className="grid select-none place-items-center border-r border-line bg-sunken px-3 text-sm font-medium text-ink-soft">
        {CC}
      </span>
      <input
        ref={ref}
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        value={local}
        onChange={(e) => handle(e.target.value)}
        placeholder={placeholder}
        required={required}
        autoFocus={autoFocus}
        // Room for a pasted "+91 98765 43210"; typedLocal keeps 10 digits.
        maxLength={20}
        pattern="[6-9][0-9]{9}"
        className="w-full border-0 bg-transparent px-3 py-2.5 text-sm text-ink outline-hidden placeholder:text-ink-faint focus:ring-0"
      />
    </div>
  );

  const wrapped = (
    <>
      {field}
      {invalid && (
        <span className="text-xs font-medium text-danger">Must be 10 digits starting with 6, 7, 8, or 9.</span>
      )}
    </>
  );

  if (!label) return wrapped;
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="label mb-0">
        {label}
        {required && <span className="ml-0.5 text-danger" aria-hidden="true">*</span>}
      </span>
      {wrapped}
    </label>
  );
}
